import { useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";

import { SearchApiCommunicator } from "api/communicators";

/**
 * Hook for interfacing with @see {SearchApiCommunicator}, manages internal
 * state for loading, results, query, configurable via
 *
 * @param {boolean} callOnQueryChange - Search is automatically called when the query changes
 * @param {number} queryChangeCallTimeout - MS to debounce the automatic call to search on query change
 * @param {"model" | "dataset" | "mlmodel"} index
 * @param {function} onError - Callback to fire when search encounters an error in API transit. NOTE: This must
 * be wrapped in a `useCallback`.  Not doing this means we pass anon func to hook, which is regarded as a "change"
 * and can produce an infinite loop.
 *
 * @returns {{
 * query: string;
 * setQuery: (q: string) => void;
 * results: Object[];
 * loading: boolean;
 * search: (q: string) => Promise<void>
 * }}
 */
export function useSearch(
  callOnQueryChange = true,
  queryChangeCallTimeout = 500,
  index = "model",
  onError,
) {
  const [searchApiCommunicator] = useState(() => new SearchApiCommunicator());
  const [results, setResults] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const searchAndUpdateList = useCallback(
    async (value) => {
      if (value.length === 0) {
        return;
      }
      setLoading(true);
      try {
        const searchResult = await searchApiCommunicator.search(value, index);
        let results = [];
        if (searchResult.data.items.length > 0) {
          results = searchResult.data.items;
        }
        setResults(results);
      } catch (err) {
        onError && onError(err.message);
      }

      setLoading(false);
    },
    [searchApiCommunicator, onError, index]
  );

  useEffect(() => {
    if (!query || !callOnQueryChange) {
      return;
    }
    const debouncedSearch = debounce(() => {
      searchAndUpdateList(query);
    }, queryChangeCallTimeout);

    debouncedSearch();

    return () => {
      debouncedSearch.cancel();
    };
  }, [query, searchAndUpdateList, callOnQueryChange, queryChangeCallTimeout]);

  return { query, setQuery, results, loading, search: searchAndUpdateList };
}
